import React from 'react';
import { Box, Paper, Skeleton } from '@mui/material';

export const PRODUCT_CARD_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    md: 'repeat(3, 1fr)',
    lg: 'repeat(4, 1fr)',
    xl: 'repeat(5, 1fr)',
  },
  '@media (min-width: 1920px)': {
    gridTemplateColumns: 'repeat(6, 1fr)',
  },
  gap: { xs: 1.25, sm: 1.75 },
  p: { xs: 1.25, sm: 2 },
  bgcolor: 'grey.50',
} as const;

export const SKELETON_CARD_COUNT = 12;
export const SKELETON_LIST_COUNT = 8;

const cardShellSx = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
} as const;

export function ProductCardSkeleton({ showStoreChrome = false }: { showStoreChrome?: boolean }) {
  return (
    <Paper sx={cardShellSx} aria-hidden>
      <Box
        sx={{
          position: 'relative',
          height: 150,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <Skeleton variant="rectangular" animation="wave" height="100%" width="100%" />
        {showStoreChrome && (
          <>
            <Skeleton
              variant="rounded"
              animation="wave"
              width={18}
              height={18}
              sx={{ position: 'absolute', top: 8, left: 8, borderRadius: '4px' }}
            />
            <Skeleton
              variant="rounded"
              animation="wave"
              width={72}
              height={22}
              sx={{ position: 'absolute', top: 8, right: 8, borderRadius: '11px' }}
            />
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, p: 1.75 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Skeleton variant="rounded" animation="wave" width={18} height={18} />
            <Skeleton variant="text" animation="wave" width={72} height={14} />
          </Box>
          <Skeleton variant="text" animation="wave" width="88%" height={18} />
          <Skeleton variant="text" animation="wave" width="64%" height={16} />
        </Box>
        <Skeleton variant="text" animation="wave" width={108} height={22} />
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1.125, pt: 1.375, borderTop: '1px solid', borderColor: 'grey.100' }}>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Skeleton variant="rounded" animation="wave" width={56} height={20} />
            <Skeleton variant="rounded" animation="wave" width={72} height={20} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Skeleton variant="text" animation="wave" width={80} height={14} />
            <Skeleton variant="rounded" animation="wave" width={64} height={26} />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export function ProductCardSkeletonGrid({
  count = SKELETON_CARD_COUNT,
  showStoreChrome = false,
}: {
  count?: number;
  showStoreChrome?: boolean;
}) {
  return (
    <Box sx={PRODUCT_CARD_GRID_SX} aria-busy="true" aria-label="Loading products">
      {Array.from({ length: count }, (_, i) => (
        <ProductCardSkeleton key={i} showStoreChrome={showStoreChrome} />
      ))}
    </Box>
  );
}

function listGridColumns(isStoreScoped: boolean): string {
  return isStoreScoped
    ? '36px 44px minmax(0,3fr) minmax(0,1.1fr) minmax(0,1fr) minmax(0,1.15fr) minmax(0,0.95fr)'
    : '44px minmax(0,3fr) minmax(0,1.1fr) minmax(0,1fr) minmax(0,1.15fr) minmax(0,0.95fr)';
}

export function ProductListSkeleton({
  count = SKELETON_LIST_COUNT,
  isStoreScoped = false,
}: {
  count?: number;
  isStoreScoped?: boolean;
}) {
  const columns = { xs: 'minmax(0,1fr)', md: listGridColumns(isStoreScoped) };
  return (
    <Box sx={{ bgcolor: 'grey.50' }} aria-busy="true" aria-label="Loading products">
      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: columns,
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.125,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {isStoreScoped && <Box />}
        <Box />
        <Box>Product</Box>
        <Box>Category</Box>
        <Box sx={{ textAlign: 'right' }}>Price</Box>
        <Box>Flags</Box>
        <Box sx={{ textAlign: 'right' }}>Data</Box>
      </Box>
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          aria-hidden
          sx={{
            display: 'grid',
            gridTemplateColumns: columns,
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 1, md: 1.5 },
            px: 2.5,
            py: 1,
            minHeight: 54,
            borderBottom: '1px solid',
            borderColor: 'grey.100',
            bgcolor: 'background.paper',
          }}
        >
          {isStoreScoped && <Skeleton variant="rounded" animation="wave" width={18} height={18} />}
          <Skeleton variant="rounded" animation="wave" width={44} height={38} />
          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton variant="text" animation="wave" width="70%" height={16} />
            <Skeleton variant="text" animation="wave" width="48%" height={14} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Skeleton variant="text" animation="wave" width="80%" height={16} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <Skeleton variant="text" animation="wave" width={72} height={16} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75 }}>
            <Skeleton variant="rounded" animation="wave" width={56} height={20} />
            <Skeleton variant="rounded" animation="wave" width={64} height={20} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <Skeleton variant="text" animation="wave" width={88} height={14} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
